import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, Download, Eye, EyeOff, Loader2, Share2, AtSign } from 'lucide-react';
import { useNativeShare } from '../../hooks/useNativeShare';
import { buildCardModel } from '../../lib/comparisonCard';
import { makeBlurredBackdrop } from '../../lib/blurBackdrop';
import SplitCard from './templates/SplitCard';
import EditorialCard from './templates/EditorialCard';
import WipeCard from './templates/WipeCard';
import StackedCard from './templates/StackedCard';
import QuadGridCard from './templates/QuadGridCard';
import TwinWipeCard from './templates/TwinWipeCard';

const RATIOS = [
  { id: 'story',  w: 1080, h: 1920, label: '9:16', form: 'portrait' },
  { id: 'feed',   w: 1080, h: 1350, label: '4:5',  form: 'portrait' },
  { id: 'square', w: 1080, h: 1080, label: '1:1',  form: 'square'   },
  { id: 'wide',   w: 1920, h: 1080, label: '16:9', form: 'wide'     },
];

// `pairs` gates which templates are offered: 1-pair (2-photo) comparisons get
// the four single-pair templates, 2-pair (4-photo) comparisons get the two
// quad ones. See `available` below — a template built for one shape cannot
// render the other, so a stale selection must never cross the boundary.
const TEMPLATES = [
  { id: 'split',     Component: SplitCard,     labelKey: 'comparisonShare.templateSplit',     pairs: 1 },
  { id: 'editorial', Component: EditorialCard, labelKey: 'comparisonShare.templateEditorial', pairs: 1 },
  { id: 'wipe',      Component: WipeCard,      labelKey: 'comparisonShare.templateWipe',      pairs: 1 },
  { id: 'stacked',   Component: StackedCard,   labelKey: 'comparisonShare.templateStacked',   pairs: 1 },
  { id: 'quadgrid',  Component: QuadGridCard,  labelKey: 'comparisonShare.templateQuadGrid',  pairs: 2 },
  { id: 'twinwipe',  Component: TwinWipeCard,  labelKey: 'comparisonShare.templateTwinWipe',  pairs: 2 },
];

// Locale used for the exported card's date strings, keyed by this app's
// 2-letter i18n language codes (see src/i18n/index.js supportedLngs).
const DATE_LOCALES = { en: 'en-GB', pt: 'pt-PT', es: 'es-ES' };

// Every pose id the two pickers can produce, mapped to the translation keys the
// pickers themselves use (CheckInDetail.js and MeasurementsPage.js). The pose
// name is baked into the exported image, so a missing entry here is publicly
// visible — keep this in step with ALL_POSES in both files.
const POSE_LABEL_KEYS = {
  front: 'clientCheckIns.posesFrontRelaxed',
  side: 'clientCheckIns.posesSideRelaxed',
  back: 'clientCheckIns.posesBackRelaxed',
  front_flex: 'clientCheckIns.posesFrontFlex',
  side_flex: 'clientCheckIns.posesSideFlex',
  back_flex: 'clientCheckIns.posesBackFlex',
  relaxed: 'clientCheckIns.posesFavorite',
};

// Load an image just far enough to read its natural size. Photos are data URLs
// already in memory, so this cannot fail on the network.
function loadNatural(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export default function ComparisonShareDialog({
  open,
  onClose,
  viewerProps,
  defaultTemplate = 'split',
  coachMode = false,
}) {
  const { t, i18n } = useTranslation();
  const { shareImage, downloadImage } = useNativeShare();

  // `pairs` is not itself state — it's derived fresh from viewerProps every
  // render, same as any other prop-derived value. Memoized (rather than a
  // plain `|| []`) because a fresh empty-array identity on every render would
  // make every useMemo below that depends on `pairs` recompute unconditionally
  // — react-hooks/exhaustive-deps (an ERROR in this build) flags exactly that.
  // pairCount only ever reads as 1 or 2: a single 4-photo pair set always
  // arrives as two pairs.
  const pairs = useMemo(() => viewerProps?.pairs || [], [viewerProps]);
  const pairCount = pairs.length > 1 ? 2 : 1;
  const available = TEMPLATES.filter(x => x.pairs === pairCount);

  const [templateId, setTemplateId] = useState(defaultTemplate);
  const [ratioId, setRatioId] = useState('story');
  const [showNumbers, setShowNumbers] = useState(!coachMode);
  const [showHandle, setShowHandle] = useState(!coachMode);
  const [prep, setPrep] = useState(null);
  const [busy, setBusy] = useState(null);
  const [capturing, setCapturing] = useState(false);
  // Drives the preview's scale cap. Read at mount and kept live via a resize
  // listener so rotating a phone (or resizing a desktop window) re-scales the
  // preview instead of freezing it at whatever size the dialog first opened.
  const [viewport, setViewport] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  const captureRef = React.useRef(null);
  const pending = React.useRef(null);

  // Reset the template selection whenever the caller's default or the pair
  // count changes, so a `split` selection (1-pair) can never survive into
  // 4-photo mode where only quadgrid/twinwipe are valid — see `available`
  // below and the `template` fallback, which is the second independent guard.
  useEffect(() => {
    setTemplateId(defaultTemplate);
  }, [defaultTemplate, pairCount]);

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Async prep: natural sizes (for the explicit geometry) and pre-blurred
  // backdrops (because html2canvas cannot blur), for EVERY photo slot across
  // every pair — not just a fixed before/after. Keyed on a joined URL string
  // (not the `pairs` array identity, which changes every render) so this only
  // re-runs when the actual set of photos changes.
  const slotUrls = useMemo(
    () => pairs.flatMap(p => [p.before.url, p.after.url]).filter(Boolean),
    [pairs],
  );
  const slotKey = slotUrls.join('|');

  useEffect(() => {
    if (!open || slotUrls.length === 0) return undefined;
    let cancelled = false;
    setPrep(null);
    (async () => {
      const entries = await Promise.all(slotUrls.map(async (url) => {
        const [natural, backdropUrl] = await Promise.all([loadNatural(url), makeBlurredBackdrop(url)]);
        return [url, { natural, backdropUrl }];
      }));
      if (!cancelled) setPrep(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slotKey]);

  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const ratio = RATIOS.find(r => r.id === ratioId) || RATIOS[0];
  const template = available.find(x => x.id === templateId) || available[0];

  // The card's own wording is localised here — it is baked into the exported
  // image, so a Portuguese client's shared card must not say BEFORE/AFTER.
  // elapsedLabel's units and the date locale used to live as English/en-GB
  // literals inside lib/comparisonCard.js (a pure, i18n-free module) — they
  // are supplied from here instead, same as every other card string.
  const cardLabels = useMemo(() => ({
    before: t('comparisonShare.cardBefore', 'BEFORE'),
    after: t('comparisonShare.cardAfter', 'AFTER'),
    transformation: t('comparisonShare.cardTransformation', 'TRANSFORMATION'),
    start: t('comparisonShare.cardStart', 'START'),
    now: t('comparisonShare.cardNow', 'NOW'),
    bodyFat: t('comparisonShare.cardBodyFat', 'BODY FAT'),
    bodyFatShort: t('comparisonShare.cardBodyFatShort', '% BF'),
    day: t('comparisonShare.cardDay', 'day'),
    days: t('comparisonShare.cardDays', 'days'),
    week: t('comparisonShare.cardWeek', 'week'),
    weeks: t('comparisonShare.cardWeeks', 'weeks'),
    month: t('comparisonShare.cardMonth', 'month'),
    months: t('comparisonShare.cardMonths', 'months'),
    locale: DATE_LOCALES[i18n.language] || 'en-GB',
    // Must cover all SEVEN poses the pickers offer, not just three. An earlier
    // attempt used `checkIns.${poseType}`, which only has front/side/back — the
    // four flex/relaxed poses fell through to the raw key and baked
    // "FRONT_FLEX", underscore and all, into a public image. clientCheckIns.poses*
    // is the namespace both pose pickers actually use and is translated in
    // en/pt/es. The `.replace(/_/g, ' ')` fallback preserves the pre-existing
    // behaviour if a new pose id ever lands without a translation.
    // A map, not a single label, because comparisonCard.js reads
    // labels.poseNames per pair — a 4-photo card can mix poses across its two
    // pairs, so every pose id must resolve regardless of which one is showing.
    poseNames: Object.fromEntries(
      Object.entries(POSE_LABEL_KEYS).map(([id, key]) => [id, t(key, id.replace(/_/g, ' ')).toUpperCase()])
    ),
  }), [t, i18n.language]);

  const model = useMemo(() => {
    if (!prep || pairs.length === 0) return null;
    const slot = (sideState) => ({
      url: sideState.url,
      canonical: sideState.canonical,
      manual: sideState.manual,
      natural: prep[sideState.url]?.natural ?? null,
      backdropUrl: prep[sideState.url]?.backdropUrl ?? null,
    });
    const first = pairs[0];
    return buildCardModel({
      pairs: pairs.map(p => ({
        poseType: { before: p.before.poseType, after: p.after.poseType },
        before: slot(p.before),
        after: slot(p.after),
      })),
      beforeDate: first.before.date,
      afterDate: first.after.date,
      beforeWeightKg: first.before.weightKg,
      beforeBodyFat: first.before.bodyFat,
      afterWeightKg: first.after.weightKg,
      afterBodyFat: first.after.bodyFat,
      sliderPos: viewerProps.sliderPos,
      handle: viewerProps.handle,
      labels: cardLabels,
      privacy: { showNumbers, showHandle },
    });
  }, [pairs, prep, viewerProps, cardLabels, showNumbers, showHandle]);

  const capture = useCallback(async (mode) => {
    if (!captureRef.current) {
      // Same cleanup as the finally below — without this, `busy` (which
      // disables Back, both pickers and both action buttons) never clears
      // and the dialog is stuck locked.
      pending.current = null;
      setCapturing(false);
      setBusy(null);
      return;
    }
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(captureRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        width: ratio.w,
        height: ratio.h,
      });
      const fileName = `trainedbyrs-comparison-${template.id}-${ratio.id}-${Date.now()}.png`;
      // Full-resolution canvas straight out — never downscale before export.
      if (mode === 'share') {
        await shareImage(canvas, {
          title: t('comparisonShare.shareTitle', 'My Transformation'),
          text: t('comparisonShare.shareText', 'Tracked on trainedbyRS'),
          fileName,
        });
      } else {
        downloadImage(canvas, fileName);
      }
    } catch (e) {
      if (e?.name !== 'AbortError') {
        console.error('Comparison card export failed', e);
        toast.error(t('comparisonShare.exportFailed', 'Could not create the image. Try a smaller size.'));
      }
    } finally {
      pending.current = null;
      setCapturing(false);
      setBusy(null);
    }
  }, [ratio, template, shareImage, downloadImage, t]);

  // The full-resolution tree mounts only during capture.
  // Depends on `capturing` alone, deliberately. `capture` is a useCallback whose
  // identity changes with ratio/template, so including it lets a picker click
  // mid-capture re-enter this effect while `capturing` is still true — two
  // concurrent html2canvas runs over a DOM node that is being re-rendered
  // underneath them. This mirrors ProgressStoryGenerator.js:736, which solves
  // the same problem the same way.
  useEffect(() => {
    if (!capturing || !pending.current) return;
    capture(pending.current);
  }, [capturing]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = (mode) => { setBusy(mode); pending.current = mode; setCapturing(true); };

  if (!open) return null;

  const Card = template.Component;
  // Cap derived from the viewport rather than a fixed constant — at
  // 1440x900 (coach desktop, CLAUDE.md's primary coach viewport) a fixed
  // 420px cap scaled the preview to ~22% with ~250px of dead space above and
  // below, too small to read what's about to be published. 640px keeps very
  // tall/short windows from producing an oversized preview.
  const previewMaxH = Math.min(viewport.h * 0.6, 640);
  const previewScale = Math.min(previewMaxH / ratio.h, (viewport.w - 48) / ratio.w);

  return ReactDOM.createPortal(
    // z-[400]: this opens ON TOP of a shadcn Dialog (z-[300]) in the coach
    // flow. At the default z-50 it renders UNDER the dialog overlay and the
    // page looks frozen — the v3.8.281 bug.
    // pointer-events-auto is load-bearing, not decoration. Radix sets
    // `pointer-events: none` on <body> while its Sheet/Dialog is open and
    // re-enables it only on its own content. This dialog portals to <body> as a
    // sibling, so it INHERITS none: in the coach flow it rendered perfectly on
    // top at z-[400] and every control — Save, Share, Back, the pickers — was
    // dead to clicks. Same "app looks frozen" symptom as v3.8.281, different
    // mechanism, and invisible to any test that only checks what is painted.
    <div className="fixed inset-0 z-[400] pointer-events-auto bg-[#111] flex flex-col text-white">
      <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3 border-b border-white/10">
        <button
          onClick={onClose}
          disabled={!!busy}
          className="p-2 rounded-full hover:bg-white/10 disabled:opacity-40 disabled:pointer-events-none"
          aria-label={t('common.back', 'Back')}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="text-base font-bold">{t('comparisonShare.title', 'Share comparison')}</div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto flex items-center justify-center p-4">
        {!model ? (
          <Loader2 className="h-8 w-8 animate-spin text-white/60" />
        ) : (
          <div
            className="rounded-xl overflow-hidden shadow-2xl"
            style={{ width: ratio.w * previewScale, height: ratio.h * previewScale }}
          >
            <div style={{ transform: `scale(${previewScale})`, transformOrigin: 'top left', width: ratio.w, height: ratio.h }}>
              <Card model={model} W={ratio.w} H={ratio.h} form={ratio.form} />
            </div>
          </div>
        )}
      </div>

      {/* Hidden full-resolution render — mounts only during capture */}
      {capturing && model && (
        <div style={{ position: 'fixed', left: -99999, top: 0, width: ratio.w, height: ratio.h }} aria-hidden="true">
          <div ref={captureRef}>
            <Card model={model} W={ratio.w} H={ratio.h} form={ratio.form} />
          </div>
        </div>
      )}

      <div className="flex-shrink-0 border-t border-white/10 px-3 py-3 space-y-3">
        <div className="flex gap-2 overflow-x-auto">
          {available.map(tpl => (
            <button
              key={tpl.id}
              onClick={() => setTemplateId(tpl.id)}
              disabled={!!busy}
              className={`px-3 h-11 md:h-9 rounded-lg text-xs whitespace-nowrap disabled:opacity-40 ${
                templateId === tpl.id ? 'bg-primary text-white' : 'bg-white/10 text-white/70'
              }`}
            >
              {t(tpl.labelKey)}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          {RATIOS.map(r => (
            <button
              key={r.id}
              onClick={() => setRatioId(r.id)}
              disabled={!!busy}
              className={`flex-1 h-11 md:h-9 rounded-lg text-xs disabled:opacity-40 ${
                ratioId === r.id ? 'bg-primary text-white' : 'bg-white/10 text-white/70'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setShowNumbers(v => !v)}
            aria-pressed={showNumbers}
            disabled={!!busy}
            className={`flex-1 h-11 md:h-9 rounded-lg text-xs flex items-center justify-center gap-1.5 disabled:opacity-40 ${
              showNumbers ? 'bg-white/15 text-white' : 'bg-white/5 text-white/50'
            }`}
          >
            {showNumbers ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            {t('comparisonShare.numbers', 'Numbers')}
          </button>
          <button
            onClick={() => setShowHandle(v => !v)}
            aria-pressed={showHandle}
            disabled={!!busy}
            className={`flex-1 h-11 md:h-9 rounded-lg text-xs flex items-center justify-center gap-1.5 disabled:opacity-40 ${
              showHandle ? 'bg-white/15 text-white' : 'bg-white/5 text-white/50'
            }`}
          >
            <AtSign className="h-4 w-4" />
            {t('comparisonShare.handle', 'Handle')}
          </button>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => run('download')}
            disabled={!model || !!busy}
            className="flex-1 h-12 rounded-lg bg-white/10 flex items-center justify-center gap-2 disabled:opacity-40"
          >
            {busy === 'download' ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
            {t('comparisonShare.save', 'Save')}
          </button>
          <button
            onClick={() => run('share')}
            disabled={!model || !!busy}
            className="flex-1 h-12 rounded-lg bg-[#FF5C00] flex items-center justify-center gap-2 disabled:opacity-40"
          >
            {busy === 'share' ? <Loader2 className="h-5 w-5 animate-spin" /> : <Share2 className="h-5 w-5" />}
            {t('comparisonShare.share', 'Share')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
